import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, OnChanges, Injector, } from "@angular/core";
import { SCFPrivileges, ScfV2Service } from "../../../scf-v2.service";
import { EnvironmentService } from "src/app/services/environment/environment.service";
import { TesterToolsService } from "src/app/services/environment/tester-tools.service";
import { FormStatusEnum, SignatureAction } from "src/app/common/enumerations/enumerations";
import { CancelDialogComponent } from "src/app/controls/cancel-dialog/cancel-dialog.component";
import { SCFHoldPointResource, SCFMaster } from "../../../scf";
import { BaseComponent } from "src/app/common/base/base.component";
import { WfSignature, WfSignatureResource, WFTaskSignatureResource } from "src/app/components/workflow/workflow";
import { FormViewState } from "src/app/common/models/form-view-state";

@Component({
  selector: "scf-v2-main-s4-nowork",
  templateUrl: "./scf-v2-main-s4-nowork.component.html",
  styleUrls: ["./scf-v2-main-s4-nowork.component.scss"],
})
export class ScfV2MainS4NoworkComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() scfMaster?: SCFMaster | null;
  @Input() disabled? = true;
  @Input() privileges?: SCFPrivileges;
  @Input() status?: FormViewState;

  @Output() changed = new EventEmitter<SCFMaster>();

  disableControls = true;

  signatures?: WfSignature[];
  wfSignatures?: WfSignatureResource[];

  noWorkPerformedTitle?: string;
  noWorkPerformedLegend?: string;

  holdPoints?: SCFHoldPointResource[];

  requesterAmended?: boolean;

  constructor(
    private scfService: ScfV2Service,
    private environmentService: EnvironmentService,
    private testerToolsService: TesterToolsService,
    protected override injector: Injector
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.setDisabled();
    this.getValues();
  }

  ngOnInit(): void {
    this.initializeForm();
    this.setMessages();
  }

  initializeForm() { }

  setMessages() {
    this.noWorkPerformedTitle = this.getMessage(
      "noWorkPerformedTitle"
    ).description;
    this.noWorkPerformedLegend = this.getMessage(
      "noWorkPerformedLegend"
    ).description;
  }

  setDisabled() {
    this.disabled =
      (this.scfMaster?.status !== FormStatusEnum.Active &&
        this.scfMaster?.status !== FormStatusEnum.HoldPoint &&
        this.scfMaster?.status !== FormStatusEnum.PendingCancelation) ||
      !this.getCurrentUser();
  }

  getValues() {
    this.wfSignatures = [];
    this.signatures = [];
    if (this.scfMaster) {
      this.createSignatures();
    }
  }

  createSignatures() {
    let index = 0;
    this.wfSignatures = this.scfMaster?.wfTable?.wfTableLocal?.wfSectionLocals
      ?.find((x) => x.number == 3)
      ?.wfSignatures?.filter((x) => x.wfTaskLocal.wfStepLocal?.code == "NO_WORK")
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const requesterSignature = this.wfSignatures?.find(x => x.requesterSignature);
    if (this.status?.isViewingAmendment) {
      const amendment = this.scfMaster?.scfAmendments?.find(x => x.active);
      this.requesterAmended = this.scfMaster?.requesterId != amendment?.requesterId;
      if (this.requesterAmended && requesterSignature && amendment) {
        requesterSignature.users = [];
        const user = this.getUser(amendment.requesterId);
        if (user)
          requesterSignature.users.push(user);
      }
    }
    else {
      if (requesterSignature && this.scfMaster?.requesterId) {
        requesterSignature.users = [];
        const user = this.getUser(this.scfMaster?.requesterId);
        if (user)
          requesterSignature.users.push(user);
      }
    }
    if (this.wfSignatures) {
      for (const t of this.wfSignatures) {
        const signature: WfSignature = {
          id: t.id,
          type: t.type,
          approveActionId: t.approveID,
          unapproveActionId: t.unapproveID,
          disapproveActionId: t.disapproveID,
          amendmentId: t.amendmentID,
          amendmentNumber: t.amendmentNumber ? t.amendmentNumber : 0,
          code: t.code,
          testSignReady: t.testSignReady,
          name: t.titleBefore,
          roles: t.roles.map((r) => r?.id),
          roleCodes: t.roles.length
            ? t.roles.map((r) => r?.code).join("/")
            : undefined,
          userNames: t.users?.map((r) => r?.name)[0],
          disabled:
            this.disabled ||
            (t.requesterSignature && !t.users.map(u => u.id).includes(this.currentUser?.id)),
          approvedBy: t.signedBy,
          approved: t.signed,
          approvedOn: t.signedOn,
          approveAction:
            this.taskEnabled(t, SignatureAction.Approve) && !this.disabled,
          unapproveAction:
            this.taskEnabled(t, SignatureAction.Unnaprove) && !this.disabled,
          disapproveAction:
            this.taskEnabled(t, SignatureAction.Disapprove) &&
            !this.disabled &&
            !t.signed,
          amendment: t.amendmentEnabled,
          required: t.required,
          taskId: t.taskID,
          value: t.type == 2 ? t.signed : t.value,
          requesterSignature: t.requesterSignature,
          visible: t.visible,
          enabled: t.enabled,
          warning: false,
          sectionNumber: 3,
        };

        if (!signature.enabled) {
          signature.approvedBy = null;
          signature.approved = false;
        }
        index++;

        this.signatures?.push(signature);
      }
    }
  }

  taskEnabled(t: WfSignatureResource, a: SignatureAction) {
    let result: boolean | undefined = false;
    if (t.requesterSignature) {
      switch (a) {
        case SignatureAction.Approve:
          result =
            t.wfTaskLocal.wfTasks?.[0]?.signedBy == null &&
            this.scfMaster?.requesterId == this.getCurrentUser()?.id;
          break;

        case SignatureAction.Unnaprove:
          result =
            t.wfTaskLocal.wfTasks?.[0]?.signedBy != null &&
            this.scfMaster?.requesterId == this.getCurrentUser()?.id;
          break;
      }
    } else {
      if (this.getCurrentUser()?.id != this.scfMaster?.requesterId) {
        const reqSignature = this.wfSignatures?.find(
          (x) => x.code == "NO_WORK_CHECK"
        );
        t.enabled = reqSignature?.signed;
        result =
          this.hasRoles(t.roles) &&
          this.scfMaster?.status == FormStatusEnum.PendingCancelation &&
          t.enabled;
      }
    }
    return result;
  }

  approve(s: WfSignature, text: string | null = null) {
    const wfTaskSignature: WFTaskSignatureResource = {
      taskId: s.taskId,
      actionId: s.approveActionId,
      reason: text,
    };
    this.scfService.sign(wfTaskSignature).toPromise().then(async (res) => {
      // this.checkStatus(s);
    })
      .catch((error) => {
        console.log(error);
        this.alert.defaultError();
      });
  }

  /**
   * Whether the tester-only "auto-sign as role" tooling should be shown.
   * Gated defensively: only on non-production builds AND for System Administrators,
   * mirroring the other SCF sections so it can never surface in production.
   */
  get showTestTools(): boolean {
    return !this.environmentService.isProduction() && this.isSA() && this.testerToolsService.enabled;
  }

  /**
   * Tester convenience: whether to offer the "Tester Approve" button for a No Work Performed
   * signature. Mirrors the Work Complete / hold-point canTestSign: the button appears on the
   * signature whose turn it currently is (backend `testSignReady`) when the logged-in SA cannot
   * complete it themselves — both the requester's "No Work" check (signed as the requester) and
   * the role approvals that follow (signed as a role-holder). The server-side TestSign resolves
   * the appropriate signer in either case.
   */
  canTestSign(s: WfSignature): boolean {
    return (
      this.showTestTools &&
      s.sectionNumber === 3 &&
      !!s.testSignReady &&
      !this.disabled &&
      !!s.approveActionId &&
      !(s.approveAction && !s.disabled)
    );
  }

  /**
   * Tester tooling (non-production): applies this No Work Performed signature on behalf of the
   * appropriate user (the requester for the "No Work" check, or the first holder of one of the
   * signature's roles) via the non-prod, SA-gated TestSign endpoint. The SignalR refresh that
   * follows reloads the form; advancing the No Work path drives the form toward Closed.
   */
  async testSign(s: WfSignature) {
    if (!this.canTestSign(s)) return;
    const wfTaskSignature: WFTaskSignatureResource = {
      taskId: s.taskId,
      actionId: s.approveActionId,
    };
    this.scfService.testSign(wfTaskSignature).toPromise().then(
      (data) => {
        if (data) this.scfMaster = data;
        this.getValues();
      },
      (error) => {
        console.log(error);
        this.alert.defaultError();
      }
    );
  }

  // checkStatus(s: WfSignature) {
  //   if (!s.requesterSignature) {
  //     const signatures = this.wfSignatures?.filter(
  //       (x) => x.code != "NO_WORK_CHECK"
  //     );
  //     const allSigned = signatures?.filter((x) => !x.signed).length == 0;
  //     if (allSigned) {
  //       const wfTaskSignature: WFTaskSignatureResource = {
  //         taskId: s.taskId,
  //         actionId: s.approveActionId
  //       };
  //       this.scfService.sign(wfTaskSignature).toPromise().then(() => {
  //         this.alert.message('formStateChanged', [new MessagePlaceholder('{serialNo}', this.scfMaster?.serialNo), new MessagePlaceholder('{status}', 'Closed')]);
  //         this._olog.postToOlog(FormType.SCF, this.scfMaster?.id ?? 0, OlogNotificationType.Closed);
  //       }, error => {
  //         console.log(error);
  //         this.alert.defaultError();
  //       });
  //     }
  //   }
  // }

  disapprove(s: WfSignature, data: any) {
    const wfTaskSignature: WFTaskSignatureResource = {
      taskId: s.taskId,
      actionId: s.disapproveActionId,
      reason: data.text,
    };
    this.scfService
      .sign(wfTaskSignature)
      .toPromise()
      .then(
        (res) => {
          // this.checkStatus(s);
        },
        (error) => {
          console.log(error);
          this.alert.defaultError();
        }
      );
  }

  unapprove(s: WfSignature) {
    const wfTaskSignature: WFTaskSignatureResource = {
      taskId: s.taskId,
      actionId: s.unapproveActionId,
    };
    this.scfService
      .sign(wfTaskSignature)
      .toPromise()
      .then(
        async (res: any) => {
          // this.checkStatus(s);
        },
        (error: any) => {
          console.log(error);
          this.alert.defaultError();
        }
      );
  }

  checked(s: WfSignature, e: any) {
    if (e) {
      if (s.requesterSignature) {
        const confirm = this.dialog.open(CancelDialogComponent, {
          width: "600px",
          data: {
            label: this.getMessage("EnterAReasonToCloseSCF").description,
            text: "",
            inputType: "text",
          },
        });
        confirm.afterClosed().subscribe((data) => {
          if (data.text) {
            this.approve(s, data.text);
          } else {
            this.getValues();
          }
        });
      } else {
        this.approve(s);
      }
    } else {
      this.unapprove(s);
    }
  }
}
